import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import FocusedDialog from '../ui/FocusedDialog';
import Button from '../ui/Button';
import { X, Upload, AlertCircle, FileText, Trash2 } from 'lucide-react';
import { BacktestResponse, MarketplaceListing } from '../../types/marketplace';
import marketplaceService from '../../services/marketplaceService';
import { isWhiteLabel } from '../../config/tenantConfig';
import { useConfirm } from '../common/useConfirm';

const ACCENT_TEXT = isWhiteLabel() ? 'text-zebu-600' : 'text-blue-600';
const SUBMIT_BTN = isWhiteLabel() ? 'bg-zebu-600 hover:bg-zebu-700' : 'bg-blue-600 hover:bg-blue-700';

interface Props {
  listing: MarketplaceListing;
  onClose: () => void;
  onSuccess: (updated: MarketplaceListing, stats: BacktestResponse | null) => void;
}

const MAX_PREVIEW_ROWS = 5;

const UploadBacktestDialog: React.FC<Props> = ({ listing, onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string[][] | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { askConfirm, confirmDialog } = useConfirm();

  const hasExisting = !!listing.backtest_uploaded_at;

  const handleFile = async (selected: File) => {
    setError(null);
    setFile(selected);
    try {
      const text = await selected.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      setRowCount(Math.max(0, lines.length - 1));
      setPreview(lines.slice(0, MAX_PREVIEW_ROWS + 1).map((l) => l.split(',')));
    } catch {
      setPreview(null);
      setRowCount(0);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a CSV file'); return; }
    setError(null);
    setIsSubmitting(true);
    try {
      const resp = await marketplaceService.uploadBacktest(listing.basket_id, file);
      const updated: MarketplaceListing = {
        ...listing,
        backtest_uploaded_at: resp.backtest_uploaded_at,
        backtest_row_count: resp.backtest_row_count,
        backtest_start_date: resp.backtest_start_date,
        backtest_end_date: resp.backtest_end_date,
        backtest_cagr: resp.stats.cagr,
        backtest_total_return: resp.stats.totalReturn,
        backtest_win_rate: resp.stats.winRate,
        backtest_sharpe_ratio: resp.stats.sharpeRatio,
        backtest_net_pnl: resp.stats.netPnl,
        backtest_max_drawdown: resp.stats.maxDrawdown,
      };
      onSuccess(updated, resp);
    } catch (err: any) {
      setError(err.message || 'Failed to upload backtest');
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const ok = await askConfirm({
      title: 'Delete Backtest',
      message: `Delete the uploaded backtest for "${listing.title}"? This removes the CSV and precomputed stats permanently.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    setError(null);
    setIsDeleting(true);
    try {
      await marketplaceService.deleteBacktest(listing.basket_id);
      const updated: MarketplaceListing = {
        ...listing,
        backtest_uploaded_at: undefined,
        backtest_row_count: undefined,
        backtest_start_date: undefined,
        backtest_end_date: undefined,
        backtest_cagr: undefined,
        backtest_total_return: undefined,
        backtest_win_rate: undefined,
        backtest_sharpe_ratio: undefined,
        backtest_net_pnl: undefined,
        backtest_max_drawdown: undefined,
      };
      onSuccess(updated, null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete backtest');
      setIsDeleting(false);
    }
  };

  const busy = isSubmitting || isDeleting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <FocusedDialog
        ariaLabel="Upload backtest data"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
      >
        <Card className="border-0 shadow-none">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Upload className={`h-5 w-5 ${ACCENT_TEXT}`} />
                Backtest Data
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => !busy && onClose()} disabled={busy} className="h-8 w-8 p-0">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Upload daily P&amp;L for <span className="font-medium">{listing.title}</span>. CSV must have exactly two columns: <code className="text-xs px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700">Date,Profit</code>.
            </p>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
                  </div>
                </div>
              )}

              {hasExisting && !file && (
                <div className="p-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-gray-600 dark:text-gray-300">
                        <p className="font-medium text-gray-900 dark:text-white mb-0.5">Current backtest</p>
                        <p>{listing.backtest_row_count} rows · {listing.backtest_start_date} → {listing.backtest_end_date}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDelete}
                      disabled={busy}
                      leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                      className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                    >
                      {isDeleting ? 'Deleting…' : 'Delete'}
                    </Button>
                  </div>
                </div>
              )}

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  CSV file {hasExisting ? '(replaces current)' : ''}
                </span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  disabled={busy}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                  className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-200 hover:file:bg-gray-200 dark:hover:file:bg-gray-600"
                />
              </label>

              {preview && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Preview</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{rowCount} data rows</span>
                  </div>
                  <table className="min-w-full text-xs">
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {preview.map((row, i) => (
                        <tr key={i} className={i === 0 ? 'bg-gray-50 dark:bg-gray-900/40 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider' : ''}>
                          {row.map((c, j) => (
                            <td key={j} className="px-3 py-1.5 text-gray-700 dark:text-gray-200 tabular-nums">{c}</td>
                          ))}
                        </tr>
                      ))}
                      {rowCount > MAX_PREVIEW_ROWS && (
                        <tr>
                          <td colSpan={2} className="px-3 py-1.5 text-gray-400 dark:text-gray-500 italic text-center">
                            … and {rowCount - MAX_PREVIEW_ROWS} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => !busy && onClose()} disabled={busy} className="flex-1">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={busy || !file}
                  className={`flex-1 ${SUBMIT_BTN} text-white`}
                  leftIcon={<Upload className="h-4 w-4" />}
                >
                  {isSubmitting ? 'Uploading…' : hasExisting ? 'Replace' : 'Upload'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </FocusedDialog>
      {confirmDialog}
    </div>
  );
};

export default UploadBacktestDialog;